import React, { useState } from "react";
import { Modal, Button, Badge } from "react-bootstrap";
import moment from "moment";
import { Printer, X, Calendar, Clock, FileText, User } from "lucide-react";

const ExcuseDutyModal = ({ show, onClose, data, onPrint }) => {
  const [printing, setPrinting] = useState(false);
  if (!data) return null;

  const isAccepted = (data.status || "").toLowerCase() === "accepted";
  const statusVariant = isAccepted ? "success" : (data.status || "").toLowerCase() === "pending" ? "warning" : "danger";
  const diagnosisText = Array.isArray(data.diagnosis) && data.diagnosis.length
    ? data.diagnosis.map((d) => `${d.diagnosis?.type || ""} ${d.diagnosis?.code ? `(${d.diagnosis.code})` : ""} ${d.diagnosis?.description || ""}`.trim()).join(", ")
    : "No diagnosis recorded";
  const period = `${data.start_date ? moment(data.start_date).format("DD-MMM-YYYY") : "--"} — ${data.end_date ? moment(data.end_date).format("DD-MMM-YYYY") : "--"}`;
  const sickDays = data.sick_days_used != null ? `${data.sick_days_used} day${data.sick_days_used === 1 ? "" : "s"}` : "--";
  const consultant = data.staff ? `${data.staff.last_name || ""} ${data.staff.first_name || ""}`.trim() || data.createdBy || "--" : data.createdBy || "--";

  const handlePrint = async () => {
    if (!isAccepted) return;
    const confirmed = window.confirm("Print this excuse duty certificate on the official letterhead?");
    if (!confirmed) return;
    try {
      setPrinting(true);
      const res = await onPrint(data.id);
      if (res?.success && res?.url) {
        window.open(res.url, "_blank");
      } else {
        alert(res?.message || "Could not generate PDF");
      }
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Print failed");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Excuse Duty Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="divider divider-start fw-medium mb-3">
          <span>Certificate Information</span>
        </div>

        <div className="d-flex align-items-center gap-2 mb-3">
          <Badge bg={statusVariant} className="text-capitalize px-3 py-2">{data.status || "Pending"}</Badge>
          <span className="text-muted small ms-2">ID: #{data.id}</span>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <div className="p-3 border rounded bg-light">
              <div className="d-flex align-items-center gap-2 mb-1 text-muted small"><Calendar size={14} /> Excuse Period</div>
              <div className="fw-semibold">{period}</div>
              <div className="small text-muted mt-1"><Clock size={12} className="me-1" />{sickDays}</div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="p-3 border rounded bg-light">
              <div className="d-flex align-items-center gap-2 mb-1 text-muted small"><Calendar size={14} /> Request Date</div>
              <div className="fw-semibold">{data.createdAt ? moment(data.createdAt).format("DD-MMM-YYYY h:mm A") : "--"}</div>
              <div className="small text-muted mt-1"><User size={12} className="me-1" />{consultant}</div>
            </div>
          </div>
        </div>

        <div className="mb-3">
          <div className="fw-semibold small text-muted mb-1 d-flex align-items-center gap-1"><FileText size={14} /> Clinical Note</div>
          <div className="p-3 border rounded" style={{ background: "#f8faf8" }}>{data.comment || "--"}</div>
        </div>

        <div className="mb-3">
          <div className="fw-semibold small text-muted mb-1">Diagnosis</div>
          <div className="p-3 border rounded" style={{ background: "#fff" }}>{diagnosisText}</div>
        </div>

        {!isAccepted && (
          <div className="alert alert-warning py-2 small mb-0">
            Only <strong>accepted</strong> excuse duties can be printed. This duty is currently <strong>{data.status || "pending"}</strong>.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} className="d-flex align-items-center gap-1">
          <X size={16} /> Close
        </Button>
        <Button
          variant="primary"
          onClick={handlePrint}
          disabled={!isAccepted || printing}
          title={!isAccepted ? "Only accepted duties can be printed" : "Print certificate"}
          className="d-flex align-items-center gap-1"
        >
          <Printer size={16} /> {printing ? "Printing..." : "Print"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExcuseDutyModal;
